import type { ReactNode } from 'react';
import { Droplets, Flame, Leaf, Snowflake, Sprout, Wind } from 'lucide-react';
import type { AgPayload } from '../types';
import BlockCard from './BlockCard';
import { blockSourceLabel, cimisSourceLabel, gridSourceLabel } from '../lib/sources';

const RISK_STYLE: Record<AgPayload['leafWetness']['risk'], string> = {
  low: 'bg-emerald-500/20 text-emerald-100 border-emerald-400/40',
  moderate: 'bg-amber-500/20 text-amber-100 border-amber-400/40',
  high: 'bg-red-500/20 text-red-100 border-red-400/40',
};

function PanelHeading({ farmName, switcher }: { farmName: string; switcher: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-xs uppercase tracking-wider opacity-70 flex items-center gap-2">
        <Sprout className="w-3.5 h-3.5" /> {farmName}
      </h2>
      {switcher}
    </div>
  );
}

export default function AgPanel({
  ag,
  loading,
  farmName,
  switcher,
}: {
  ag: AgPayload | null;
  loading: boolean;
  farmName: string;
  switcher: ReactNode;
}) {
  if (!ag && loading) {
    return (
      <section className="space-y-3">
        <PanelHeading farmName={farmName} switcher={switcher} />
        <div className="text-xs opacity-60">Loading {farmName}…</div>
      </section>
    );
  }

  if (!ag) {
    return (
      <section className="space-y-3">
        <PanelHeading farmName={farmName} switcher={switcher} />
        <div className="text-xs opacity-60 border border-white/10 rounded-lg p-3">
          {farmName} data unavailable — upstream weather service is temporarily down.
        </div>
      </section>
    );
  }

  if (ag.unavailable) {
    return (
      <section className="space-y-3">
        <PanelHeading farmName={farmName} switcher={switcher} />
        <div className="text-xs opacity-60 border border-white/10 rounded-lg p-3">
          {farmName} data unavailable — upstream weather service is temporarily down.
        </div>
      </section>
    );
  }

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleString('en-US', {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: ag.location.timeZone,
    });

  return (
    <section className="space-y-3">
      <PanelHeading farmName={farmName} switcher={switcher} />

      {ag.frost.nextFrostHour && (
        <div className="flex items-start gap-3 border border-blue-400/50 bg-blue-900/50 rounded-lg p-3">
          <Snowflake className="w-5 h-5 shrink-0 mt-0.5 text-blue-200" />
          <div className="text-sm">
            <div className="font-medium">
              Frost expected {fmtTime(ag.frost.nextFrostHour)}
            </div>
            <div className="text-xs opacity-90">
              Min next 48h: {ag.frost.minForecast48hF ?? '—'}°F ·{' '}
              {ag.frost.hoursAtOrBelow32}h at or below 32° ·{' '}
              {ag.frost.hoursAtOrBelow36}h at or below 36°
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <Stat
          icon={<Droplets className="w-4 h-4" />}
          label="ET₀ today"
          value={`${ag.et.todayIn.toFixed(2)}"`}
          sub={
            ag.et.yesterdayIn != null
              ? `yest ${ag.et.yesterdayIn.toFixed(2)}" · 7d ${(
                  ag.et.past7DaysIn ?? 0
                ).toFixed(2)}"`
              : `${ag.et.next7DaysIn.toFixed(2)}" next 7d`
          }
        />
        <Stat
          icon={<Leaf className="w-4 h-4" />}
          label="Chill hrs"
          value={String(ag.chillHours.accumulated)}
          sub={`since ${ag.chillHours.seasonStart.slice(5)}`}
        />
        <Stat
          icon={<Flame className="w-4 h-4" />}
          label={`GDD (base ${ag.gdd.baseF}°)`}
          value={String(ag.gdd.accumulated)}
          sub={`since ${ag.gdd.seasonStart.slice(5)}`}
        />
        <Stat
          icon={<Wind className="w-4 h-4" />}
          label="Leaf wet risk"
          value={ag.leafWetness.risk}
          sub={`${ag.leafWetness.hoursHighRh24h}h RH ≥ 90%`}
          className={RISK_STYLE[ag.leafWetness.risk]}
        />
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <SoilCard
          label="Surface"
          depth={ag.soil.surface.depth}
          tempF={ag.soil.surface.tempF}
          moisture={ag.soil.surface.moistureVolPct}
        />
        <SoilCard
          label="Root zone"
          depth={ag.soil.deeper.depth}
          tempF={ag.soil.deeper.tempF}
          moisture={ag.soil.deeper.moistureVolPct}
        />
      </div>

      <div className="text-[10px] opacity-50">{gridSourceLabel(ag)}</div>

      <div className="space-y-2">
        {ag.blocks.map((b) => (
          <BlockCard
            key={b.id}
            block={b}
            sourceNote={blockSourceLabel(b, ag)}
            adminHref={`/admin?tab=farms&farm=${encodeURIComponent(ag.farm.id)}`}
          />
        ))}
      </div>

      {ag.irrigation && <IrrigationCard irr={ag.irrigation} />}

      <div className="text-[10px] opacity-50">{cimisSourceLabel(ag)}</div>

      <div>
        <div className="text-xs uppercase tracking-wider opacity-70 mb-1">
          Spray windows (next 48h)
        </div>
        {ag.sprayWindows.length === 0 ? (
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-sm opacity-80">
            No spray-friendly windows in the next 48h — wind, RH, or precip
            probability out of range.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {ag.sprayWindows.map((w) => (
              <div
                key={w.start}
                className="bg-emerald-500/20 border border-emerald-400/40 rounded-md px-2 py-1 text-xs"
              >
                <span className="font-medium">
                  {fmtTime(w.start)} – {fmtTime(w.end)}
                </span>
                <span className="opacity-80 ml-2">
                  {w.hours}h · wind {w.avgWindMph} · RH {w.avgRh}%
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="text-[10px] opacity-50 mt-1">
          Defaults: wind ≤ 10 mph, gusts ≤ 15, RH 40–85%, precip ≤ 20%.
        </div>
      </div>

      <div className="text-[10px] opacity-50">{gridSourceLabel(ag)}</div>
    </section>
  );
}

function IrrigationCard({ irr }: { irr: NonNullable<AgPayload['irrigation']> }) {
  const tone =
    irr.status === 'very-dry'
      ? 'bg-amber-500/25 border-amber-400/50'
      : irr.status === 'dry'
        ? 'bg-yellow-500/20 border-yellow-400/40'
        : irr.status === 'wet'
          ? 'bg-emerald-500/20 border-emerald-400/40'
          : 'bg-white/10 border-white/10';
  const verdict =
    irr.status === 'very-dry'
      ? 'Significant deficit — irrigate soon'
      : irr.status === 'dry'
        ? 'Dry — schedule irrigation'
        : irr.status === 'wet'
          ? 'Rainfall exceeded ET — skip irrigation'
          : 'Water balance adequate';
  return (
    <div className={`rounded-lg p-3 border backdrop-blur-sm ${tone}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-70">Irrigation (7-day)</div>
          <div className="text-sm font-medium">{verdict}</div>
        </div>
        <div className="text-right text-xs opacity-90 tabular-nums">
          <div>
            ET {irr.etPast7In.toFixed(2)}" · rain {irr.rainPast7In.toFixed(2)}"
          </div>
          {irr.replaceIn > 0 && (
            <div className="text-sm font-medium">replace ~{irr.replaceIn.toFixed(2)}"</div>
          )}
        </div>
      </div>
      <div className="text-[10px] opacity-50 mt-1">
        Rule of thumb: replace ET minus effective rain (75% on drip). Adjust for your soil + canopy.
      </div>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  sub,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  className?: string;
}) {
  return (
    <div
      className={`bg-white/10 backdrop-blur-sm rounded-lg p-2.5 border border-white/5 ${className ?? ''}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-70">
        {icon}
        {label}
      </div>
      <div className="font-medium capitalize">{value}</div>
      <div className="text-[10px] opacity-60">{sub}</div>
    </div>
  );
}

function SoilCard({
  label,
  depth,
  tempF,
  moisture,
}: {
  label: string;
  depth: string;
  tempF: number | null;
  moisture: number | null;
}) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider opacity-70 flex justify-between">
        <span>{label}</span>
        <span className="opacity-50">{depth}</span>
      </div>
      <div className="flex items-baseline gap-3 mt-1">
        <div>
          <div className="text-xs opacity-60">Temp</div>
          <div className="text-lg font-medium tabular-nums">
            {tempF ?? '—'}°
          </div>
        </div>
        <div>
          <div className="text-xs opacity-60">Moisture</div>
          <div className="text-lg font-medium tabular-nums">
            {moisture ?? '—'}%
          </div>
        </div>
      </div>
    </div>
  );
}
